import { create } from 'zustand';
import { SuperAdminUser, UserFilters, PaginationInfo, ListUsersResponse } from '../../types/super-admin';

interface UserState {
  // Data
  users: SuperAdminUser[];
  selectedUser: SuperAdminUser | null;
  
  // Filters and pagination
  filters: UserFilters;
  pagination: PaginationInfo;
  
  // UI state
  loading: boolean;
  error: string | null;
  
  // Filter options
  availableTenants: Array<{ id: string; name: string }>;
  availableRoles: string[];
  availableStatuses: string[];
  
  // Actions
  setUsers: (response: ListUsersResponse) => void;
  setSelectedUser: (user: SuperAdminUser | null) => void;
  updateUser: (userId: string, updates: Partial<SuperAdminUser>) => void;
  removeUser: (userId: string) => void;
  addUser: (user: SuperAdminUser) => void;
  
  setFilters: (filters: Partial<UserFilters>) => void;
  resetFilters: () => void;
  
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Pagination
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
}

const initialFilters: UserFilters = {
  search: '',
  tenantId: '',
  role: '',
  status: '',
  permissionTemplateId: '',
  sortBy: 'createdAt',
  sortOrder: 'desc'
};

const initialPagination: PaginationInfo = {
  page: 1,
  limit: 20,
  total: 0,
  totalPages: 0,
  hasNext: false,
  hasPrev: false
};

export const useUserStore = create<UserState>((set) => ({
  // Initial state
  users: [],
  selectedUser: null,
  filters: initialFilters,
  pagination: initialPagination,
  loading: false,
  error: null,
  availableTenants: [],
  availableRoles: [],
  availableStatuses: ['active', 'inactive', 'suspended'],
  
  // Actions
  setUsers: (response: ListUsersResponse) => {
    set({
      users: response.users,
      pagination: response.pagination,
      availableTenants: response.filters.tenants,
      availableRoles: response.filters.roles,
      availableStatuses: response.filters.statuses,
      error: null
    });
  },
  
  setSelectedUser: (user: SuperAdminUser | null) => {
    set({ selectedUser: user });
  },
  
  updateUser: (userId: string, updates: Partial<SuperAdminUser>) => {
    set(state => ({
      users: state.users.map(user => 
        user.id === userId ? { ...user, ...updates } : user
      ),
      selectedUser: state.selectedUser?.id === userId 
        ? { ...state.selectedUser, ...updates }
        : state.selectedUser
    }));
  },
  
  removeUser: (userId: string) => {
    set(state => ({
      users: state.users.filter(user => user.id !== userId),
      selectedUser: state.selectedUser?.id === userId ? null : state.selectedUser
    }));
  },
  
  addUser: (user: SuperAdminUser) => {
    set(state => ({
      users: [user, ...state.users]
    }));
  },
  
  setFilters: (newFilters: Partial<UserFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters },
      pagination: { ...state.pagination, page: 1 } // Reset to first page when filters change
    }));
  },
  
  resetFilters: () => {
    set({
      filters: initialFilters,
      pagination: { ...initialPagination }
    });
  },
  
  setLoading: (loading: boolean) => {
    set({ loading });
  },
  
  setError: (error: string | null) => {
    set({ error });
  },
  
  setPage: (page: number) => {
    set(state => ({
      pagination: { ...state.pagination, page }
    }));
  },
  
  setLimit: (limit: number) => {
    set(state => ({
      pagination: { ...state.pagination, limit, page: 1 }
    }));
  }
}));